import { useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useAudioPlayer } from "@/context/AudioContext";
import { liveRadios } from "@/mocks/liveRadios";
import { livePrograms } from "@/mocks/livePrograms";
import { radioStations } from "@/mocks/radios";
import { trendingRadios, recommendedRadios } from "@/mocks/discovery";
import { socialFeedPosts } from "@/mocks/socialFeed";
import { adminEvents } from "@/mocks/adminEvents";
import { adminContests } from "@/mocks/adminContests";
import { historyEntries } from "@/mocks/listenerHistory";
import Swal from "sweetalert2";

function formatCount(n: number): string {
  if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
  if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
  return n.toString();
}

function timeAgo(iso: string): string {
  const now = new Date();
  const d = new Date(iso);
  const diffMin = Math.floor((now.getTime() - d.getTime()) / 60000);
  if (diffMin < 1) return "Ahora";
  if (diffMin < 60) return `Hace ${diffMin}m`;
  const diffHrs = Math.floor(diffMin / 60);
  if (diffHrs < 24) return `Hace ${diffHrs}h`;
  const diffDays = Math.floor(diffHrs / 24);
  if (diffDays === 1) return "Ayer";
  return `Hace ${diffDays}d`;
}

/* ── Section Header ── */
function SectionHeader({ title, icon, count, actionLabel, onAction }: {
  title: string; icon: string; count?: string; actionLabel?: string; onAction?: () => void;
}) {
  return (
    <div className="flex items-center justify-between mb-2.5 px-4 md:px-6">
      <h2 className="text-xs font-bold text-foreground-950 flex items-center gap-2 whitespace-nowrap">
        <i className={`${icon} text-accent-500 text-[13px]`}></i>
        {title}
        {count && <span className="text-[10px] font-normal text-foreground-400 ml-1">{count}</span>}
      </h2>
      {actionLabel && onAction && (
        <button onClick={onAction} className="text-[10px] font-semibold text-primary-500 hover:text-primary-600 cursor-pointer whitespace-nowrap flex items-center gap-0.5">
          {actionLabel}
          <i className="ri-arrow-right-s-line text-xs"></i>
        </button>
      )}
    </div>
  );
}

/* ── Continue Listening ── */
function ContinueListening() {
  const navigate = useNavigate();
  const recent = useMemo(() => historyEntries.slice(0, 5), []);

  return (
    <div className="mb-5">
      <SectionHeader title="Seguir Escuchando" icon="ri-history-line" />
      <div className="flex gap-2 overflow-x-auto scrollbar-none px-4 md:px-6 pb-1">
        {recent.map((entry) => (
          <button
            key={entry.id}
            onClick={() => {
              if (entry.type === "radio") navigate("/radios");
              else if (entry.type === "programa") navigate("/programas-en-vivo");
              else navigate("/events");
            }}
            className="flex items-center gap-2.5 px-3 py-2.5 rounded-xl bg-background-50 border border-background-200 hover:border-foreground-300 shrink-0 cursor-pointer transition-all min-w-[160px] max-w-[200px]"
          >
            <div className="w-10 h-10 rounded-lg overflow-hidden bg-background-100 border border-background-200 shrink-0">
              {entry.image ? (
                <img src={entry.image} alt={entry.title} className="w-full h-full object-cover object-top" loading="lazy" />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <i className="ri-radio-line text-foreground-400 text-sm"></i>
                </div>
              )}
            </div>
            <div className="min-w-0 text-left">
              <p className="text-[11px] font-bold text-foreground-900 truncate">{entry.title}</p>
              <p className="text-[10px] text-foreground-500 truncate">{entry.subtitle}</p>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

/* ── Live Now Strip ── */
function LiveNowStrip() {
  const navigate = useNavigate();
  const { play } = useAudioPlayer();
  const live = useMemo(() => liveRadios.slice(0, 6), []);

  const handlePlay = useCallback((e: React.MouseEvent, radio: typeof liveRadios[0]) => {
    e.stopPropagation();
    const station = radioStations.find((s) => s.name === radio.name);
    if (station) {
      play(station);
      Swal.fire({ toast: true, position: "top-end", icon: "success", title: "Reproduciendo " + radio.name, showConfirmButton: false, timer: 1500, timerProgressBar: true });
    } else {
      Swal.fire({ toast: true, position: "top-end", icon: "info", title: "Radio no disponible", showConfirmButton: false, timer: 1500, timerProgressBar: true });
    }
  }, [play]);

  return (
    <div className="mb-5">
      <SectionHeader
        title="En Vivo Ahora"
        icon="ri-live-line"
        count={`${liveRadios.length} radios`}
        actionLabel="Ver todas"
        onAction={() => navigate("/radios-en-vivo")}
      />
      <div className="flex gap-3 overflow-x-auto scrollbar-none px-4 md:px-6 pb-1">
        {live.map((radio) => (
          <button
            key={radio.id}
            onClick={() => {
              const station = radioStations.find((s) => s.name === radio.name);
              const slug = station?.slug || radio.name.toLowerCase().replace(/[^a-z0-9]+/g, '-');
              navigate(`/radio/${slug}`);
            }}
            className="shrink-0 w-[140px] bg-background-50 border border-background-200 hover:border-foreground-300 rounded-2xl overflow-hidden transition-all cursor-pointer group"
          >
            <div className="relative w-full aspect-square overflow-hidden bg-background-100">
              <img src={radio.thumbnailUrl} alt={radio.name} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300" loading="lazy" />
              <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/50 via-transparent to-transparent"></div>
              <div className="absolute top-2 left-2 flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-500/90 text-white text-[8px] font-bold">
                <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse"></span>
                LIVE
              </div>
              <div className="absolute bottom-2 right-2">
                <button
                  onClick={(e) => handlePlay(e, radio)}
                  className="w-8 h-8 rounded-full bg-background-50/90 backdrop-blur-sm flex items-center justify-center hover:bg-background-50 transition-all cursor-pointer shadow-sm"
                >
                  <i className="ri-play-fill text-foreground-900 text-base ml-0.5"></i>
                </button>
              </div>
            </div>
            <div className="p-2.5 text-left">
              <p className="text-[11px] font-bold text-foreground-900 truncate">{radio.name}</p>
              <p className="text-[10px] text-foreground-500 truncate">{radio.frequency}</p>
              <p className="text-[10px] text-foreground-400 mt-0.5 truncate">{radio.programs[0]?.name || radio.category}</p>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

/* ── Featured Radios ── */
function FeaturedRadios() {
  const navigate = useNavigate();
  const featured = useMemo(() => trendingRadios.slice(0, 5), []);

  return (
    <div className="mb-5">
      <SectionHeader
        title="Radios Destacadas"
        icon="ri-star-line"
        actionLabel="Ver directorio"
        onAction={() => navigate("/radios")}
      />
      <div className="flex gap-2.5 overflow-x-auto scrollbar-none px-4 md:px-6 pb-1">
        {featured.map((radio) => {
          const station = radioStations.find((s) => s.id === radio.id);
          const freqSlug = station?.slug || radio.slug;
          return (
            <button
              key={radio.id}
              onClick={() => navigate(`/radio/${freqSlug}`)}
              className="flex items-center gap-3 px-3.5 py-3 rounded-xl bg-background-50 border border-background-200 hover:border-foreground-300 shrink-0 cursor-pointer transition-all min-w-[220px]"
            >
              <div className="w-12 h-12 rounded-xl overflow-hidden bg-background-100 border border-background-200 shrink-0">
                <img src={radio.logoUrl} alt={radio.name} className="w-full h-full object-cover object-top" loading="lazy" />
              </div>
              <div className="min-w-0 text-left">
                <div className="flex items-center gap-1.5">
                  <p className="text-[11px] font-bold text-foreground-900 truncate">{radio.name}</p>
                  {radio.isLive && (
                    <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse shrink-0"></span>
                  )}
                </div>
                <p className="text-[10px] text-foreground-500 truncate">{radio.frequency} · {radio.category}</p>
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-[10px] font-semibold text-foreground-600">{formatCount(radio.listeners)} oyentes</span>
                  {radio.radioScore > 0 && (
                    <span className="text-[10px] font-semibold text-amber-600">★ {radio.radioScore}</span>
                  )}
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

/* ── Current Programs ── */
function CurrentPrograms() {
  const navigate = useNavigate();
  const programs = useMemo(() => livePrograms.slice(0, 4), []);

  return (
    <div className="mb-5">
      <SectionHeader
        title="Programas al Aire"
        icon="ri-mic-line"
        actionLabel="Ver todos"
        onAction={() => navigate("/programas-en-vivo")}
      />
      <div className="flex gap-2.5 overflow-x-auto scrollbar-none px-4 md:px-6 pb-1">
        {programs.map((prog) => (
          <button
            key={prog.id}
            onClick={() => navigate(`/program/${prog.slug}`)}
            className="flex items-center gap-3 px-3.5 py-3 rounded-xl bg-background-50 border border-background-200 hover:border-foreground-300 shrink-0 cursor-pointer transition-all min-w-[230px]"
          >
            <div className="w-12 h-12 rounded-xl overflow-hidden bg-background-100 border border-background-200 shrink-0">
              <img src={prog.coverUrl} alt={prog.name} className="w-full h-full object-cover object-top" loading="lazy" />
            </div>
            <div className="min-w-0 text-left">
              <p className="text-[11px] font-bold text-foreground-900 truncate">{prog.name}</p>
              <p className="text-[10px] text-foreground-500 truncate">{prog.radioName} · {prog.host}</p>
              <div className="flex items-center gap-2 mt-1">
                <span className="text-[10px] font-semibold text-foreground-600">{formatCount(prog.followers)} seg.</span>
                <span className="px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-[8px] font-bold">AL AIRE</span>
              </div>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

/* ── Events Strip ── */
function EventsStrip() {
  const navigate = useNavigate();
  const upcomingEvents = useMemo(
    () => adminEvents.filter((e) => e.status === "publicado").slice(0, 5),
    []
  );

  if (upcomingEvents.length === 0) return null;

  return (
    <div className="mb-5">
      <SectionHeader
        title="Eventos"
        icon="ri-calendar-event-line"
        actionLabel="Ver agenda"
        onAction={() => navigate("/events")}
      />
      <div className="flex gap-2.5 overflow-x-auto scrollbar-none px-4 md:px-6 pb-1">
        {upcomingEvents.map((event) => (
          <button
            key={event.id}
            onClick={() => navigate("/events")}
            className="shrink-0 w-[200px] bg-background-50 border border-background-200 hover:border-foreground-300 rounded-2xl overflow-hidden transition-all cursor-pointer group"
          >
            <div className="relative w-full h-28 overflow-hidden bg-background-100">
              <img src={event.image} alt={event.title} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300" loading="lazy" />
              <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/50 via-transparent to-transparent"></div>
              <div className="absolute bottom-2 left-2 flex items-center gap-1.5 px-2 py-1 rounded-full bg-background-50/90 text-foreground-900 text-[9px] font-bold">
                <i className="ri-calendar-line text-[9px]"></i>
                {new Date(event.date).toLocaleDateString("es-ES", { day: "numeric", month: "short" })}
              </div>
            </div>
            <div className="p-2.5 text-left">
              <p className="text-[11px] font-bold text-foreground-900 truncate">{event.title}</p>
              <p className="text-[10px] text-foreground-500 truncate mt-0.5">{event.location} · {event.time}</p>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

/* ── Contests Strip ── */
function ContestsStrip() {
  const navigate = useNavigate();
  const active = useMemo(
    () => adminContests.filter((c) => c.status === "activo").slice(0, 4),
    []
  );

  if (active.length === 0) return null;

  return (
    <div className="mb-5">
      <SectionHeader
        title="Concursos Activos"
        icon="ri-gift-line"
        actionLabel="Participar"
        onAction={() => navigate("/events")}
      />
      <div className="flex gap-2.5 overflow-x-auto scrollbar-none px-4 md:px-6 pb-1">
        {active.map((contest) => (
          <button
            key={contest.id}
            onClick={() => navigate("/events")}
            className="shrink-0 w-[220px] rounded-2xl bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 hover:border-amber-300 p-3.5 transition-all cursor-pointer text-left"
          >
            <div className="flex items-center gap-2 mb-2">
              <div className="w-8 h-8 rounded-lg bg-amber-500 flex items-center justify-center shrink-0">
                <i className="ri-gift-fill text-background-50 text-sm"></i>
              </div>
              <div>
                <span className="text-[9px] font-bold text-amber-700 uppercase tracking-wide">Activo</span>
                <p className="text-[11px] font-bold text-foreground-900 truncate leading-tight">{contest.title}</p>
              </div>
            </div>
            <p className="text-[10px] font-bold text-foreground-700 truncate">{contest.prize}</p>
            <div className="flex items-center justify-between mt-2">
              <span className="text-[10px] text-foreground-500">
                {formatCount(contest.participants)} participantes
              </span>
              <span className="text-[9px] text-red-500 font-semibold">
                Cierra {new Date(contest.endDate).toLocaleDateString("es-ES", { day: "numeric", month: "short" })}
              </span>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

/* ── Personalized Recommendations ── */
function PersonalizedRecs() {
  const navigate = useNavigate();
  const recs = useMemo(() => recommendedRadios.slice(0, 6), []);

  return (
    <div className="mb-5">
      <SectionHeader
        title="Recomendado para Ti"
        icon="ri-thumb-up-line"
        actionLabel="Descubrir más"
        onAction={() => navigate("/descubrir")}
      />
      <div className="grid grid-cols-3 gap-2 px-4 md:px-6">
        {recs.map((radio) => {
          const station = radioStations.find((s) => s.id === radio.id);
          const freqSlug = station?.slug || radio.slug;
          return (
            <button
              key={radio.id}
              onClick={() => navigate(`/radio/${freqSlug}`)}
              className="bg-background-50 border border-background-200 hover:border-foreground-300 rounded-2xl overflow-hidden transition-all cursor-pointer group"
            >
              <div className="relative w-full aspect-square overflow-hidden bg-background-100">
                <img src={radio.logoUrl} alt={radio.name} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300" loading="lazy" />
                {radio.isLive && (
                  <div className="absolute top-1.5 left-1.5 flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-red-500/90 text-white text-[7px] font-bold">
                    <span className="w-1 h-1 rounded-full bg-white animate-pulse"></span>
                  </div>
                )}
              </div>
              <div className="p-2 text-left">
                <p className="text-[10px] font-bold text-foreground-900 truncate">{radio.name}</p>
                <p className="text-[9px] text-foreground-500 truncate">{radio.category}</p>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

/* ── Recent Content ── */
function RecentContent() {
  const navigate = useNavigate();
  const recent = useMemo(() => socialFeedPosts.slice(0, 3), []);

  return (
    <div className="mb-5">
      <SectionHeader title="Publicaciones Recientes" icon="ri-newspaper-line" />
      <div className="px-4 md:px-6 space-y-2">
        {recent.map((post) => {
          const stationSlug = (post.authorName || 'radio-exito').toLowerCase().replace(/[^a-z0-9]+/g, '-');
          return (
          <div
            key={post.id}
            onClick={() => navigate(`/radio/${stationSlug}`)}
            className="bg-background-50 border border-background-200 hover:border-foreground-300 rounded-2xl p-3.5 cursor-pointer transition-all"
          >
            <div className="flex items-center gap-2.5 mb-2">
              <div className="w-8 h-8 rounded-full overflow-hidden bg-background-100 border border-background-200 shrink-0">
                <img src={post.authorAvatar} alt={post.authorName} className="w-full h-full object-cover object-top" loading="lazy" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-[11px] font-bold text-foreground-900 truncate">{post.authorName}</p>
                <p className="text-[9px] text-foreground-400">{timeAgo(post.timestamp)}</p>
              </div>
            </div>
            <p className="text-[11px] text-foreground-700 line-clamp-2 leading-relaxed">{post.content}</p>
            <div className="flex items-center gap-3 mt-2 text-[10px] text-foreground-400">
              <span className="flex items-center gap-1"><i className="ri-thumb-up-line text-[11px]"></i>{formatCount(post.likes)}</span>
              <span className="flex items-center gap-1"><i className="ri-chat-1-line text-[11px]"></i>{post.comments}</span>
              <span className="flex items-center gap-1"><i className="ri-share-forward-line text-[11px]"></i>{post.shares}</span>
            </div>
          </div>
          );
        })}
      </div>
    </div>
  );
}

/* ── Following Quick Strip (compact version) ── */
function FollowingStrip({ onNavigateFollowing }: { onNavigateFollowing: () => void }) {
  const navigate = useNavigate();

  return (
    <div className="mb-5">
      <SectionHeader
        title="Siguiendo"
        icon="ri-user-follow-line"
        actionLabel="Ver todo"
        onAction={onNavigateFollowing}
      />
      <div className="flex gap-2 overflow-x-auto scrollbar-none px-4 md:px-6 pb-1">
        {[
          { type: "radios", label: "Radios", icon: "ri-radio-line", color: "border-l-primary-500", count: 8, active: 4 },
          { type: "programs", label: "Programas", icon: "ri-mic-line", color: "border-l-emerald-500", count: 6, active: 3 },
          { type: "hosts", label: "Locutores", icon: "ri-user-voice-line", color: "border-l-amber-500", count: 5, active: 2 },
        ].map((item) => (
          <button
            key={item.type}
            onClick={onNavigateFollowing}
            className={`flex items-center gap-2.5 px-3 py-2 rounded-xl bg-background-50 border border-background-200 hover:border-foreground-300 border-l-[3px] ${item.color} cursor-pointer shrink-0 transition-all`}
          >
            <div className="w-8 h-8 rounded-lg bg-background-100 flex items-center justify-center shrink-0">
              <i className={`${item.icon} text-foreground-600 text-xs`}></i>
            </div>
            <div className="text-left min-w-0">
              <p className="text-[10px] font-semibold text-foreground-900 whitespace-nowrap">{item.label}</p>
              <div className="flex items-center gap-1.5">
                <span className="text-[9px] font-bold text-foreground-500">{item.count}</span>
                <span className="w-1 h-1 rounded-full bg-accent-500 shrink-0"></span>
                <span className="text-[9px] text-accent-600 font-semibold">{item.active} al aire</span>
              </div>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

/* ── Main Feed Tab ── */
interface AppFeedTabProps {
  onNavigateFollowing: () => void;
}

export default function AppFeedTab({ onNavigateFollowing }: AppFeedTabProps) {
  const navigate = useNavigate();

  return (
    <div className="h-full overflow-y-auto bg-background-50 scrollbar-none">
      {/* Greeting + Search Bar */}
      <div className="px-4 md:px-6 pt-4 pb-2">
        <div className="flex items-center justify-between mb-3">
          <div>
            <p className="text-lg font-bold text-foreground-950">Inicio</p>
            <p className="text-[11px] text-foreground-400">Bienvenido a RFW Radio</p>
          </div>
          <button
            onClick={() => navigate('/app/notificaciones')}
            className="w-9 h-9 rounded-full bg-accent-100 flex items-center justify-center hover:bg-accent-200 cursor-pointer transition-all"
          >
            <i className="ri-notification-3-line text-accent-500 text-base"></i>
          </button>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-3 gap-2 mb-5">
          {[
            { label: "Radios Live", value: liveRadios.length.toString(), icon: "ri-live-line", color: "text-red-500", bg: "bg-red-50" },
            { label: "Oyentes", value: formatCount(liveRadios.reduce((s, r) => s + r.currentListeners, 0)), icon: "ri-headphone-line", color: "text-accent-500", bg: "bg-accent-50" },
            { label: "Programas", value: livePrograms.length.toString(), icon: "ri-mic-line", color: "text-emerald-500", bg: "bg-emerald-50" },
          ].map((stat) => (
            <div key={stat.label} className={`${stat.bg} border border-background-200 rounded-xl p-2.5 text-center`}>
              <div className={`text-lg font-bold text-foreground-950`}>{stat.value}</div>
              <div className="text-[9px] text-foreground-500 flex items-center justify-center gap-1 mt-0.5">
                <i className={`${stat.icon} ${stat.color} text-[10px]`}></i>
                {stat.label}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Operational Sections */}
      <ContinueListening />
      <LiveNowStrip />
      <FollowingStrip onNavigateFollowing={onNavigateFollowing} />
      <FeaturedRadios />
      <CurrentPrograms />
      <EventsStrip />
      <ContestsStrip />
      <PersonalizedRecs />
      <RecentContent />

      {/* Bottom spacer */}
      <div className="h-6" />
    </div>
  );
}